import { getApi } from 'apis';
import { SideDrawerWrapper } from 'components/Global';
import TableActionHeader from 'components/Global/TableActionHeader';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { toastText } from 'utils/utils';
import CustomTable from './CustomTable';
import EditMappingDrawerBody from './EditMappingDrawerBody';
import type { CategoryMappingRow } from './CustomTable/type';
import styles from './index.module.scss';

const CategoryDoseFormMappingTable = () => {
	const [tableData, setTableData] = useState<CategoryMappingRow[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [search, setSearch] = useState('');
	const [isSearchCancel, setIsSearchCancel] = useState(false);
	const [drawerAnimation, setDrawerAnimation] = useState(false);
	const [isSideDrawerOpen, setSideDrawerOpen] = useState(false);
	const [drawerTitle, setDrawerTitle] = useState('Edit Category Dose Form Mapping');
	const [editSelectedCategory, setEditSelectedCategory] = useState<CategoryMappingRow | undefined>(undefined);

	const fetchTableData = useCallback(async () => {
		setIsLoading(true);
		try {
			const [categoriesRes, mappingsRes] = await Promise.all([
				getApi('/category/get-all', { limit: 1000 }),
				getApi('/category-dose-form-mapping/get-all'),
			]);
			const categories = categoriesRes?.data?.data || [];
			const mappingsList = mappingsRes?.data?.data || [];
			const mappingByCategoryId: Record<string, { id: string; name: string }[]> = {};
			mappingsList.forEach((m: { categoryId: string; doseForms: { id: string; name: string }[] }) => {
				mappingByCategoryId[m.categoryId] = m.doseForms || [];
			});
			const merged: CategoryMappingRow[] = categories.map((c: { id: string; categoryName: string }) => ({
				id: c.id,
				categoryName: c.categoryName,
				doseForms: mappingByCategoryId[c.id] || [],
			}));
			setTableData(merged);
		} catch (error: any) {
			toastText(
				error?.response?.data?.error?.errorDescription ?? 'Failed to load data',
				'error'
			);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		fetchTableData();
	}, [fetchTableData]);

	const filteredData = search.trim()
		? tableData.filter((row) =>
				row.categoryName.toLowerCase().includes(search.trim().toLowerCase())
		  )
		: tableData;

	const performSearchHandler = (event: ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setSearch(value);
		if (value.length > 0) setIsSearchCancel(true);
		else setIsSearchCancel(false);
	};

	const removeDrawerFromDom = () => setSideDrawerOpen(false);

	const openDrawerHandler = () => {
		setDrawerAnimation(true);
		setSideDrawerOpen(true);
	};

	const closeDrawerByAnimation = () => {
		setEditSelectedCategory(undefined);
		setDrawerAnimation(false);
	};

	const setDrawerInfoHandler = (title: string) => setDrawerTitle(title);

	return (
		<>
			<div>
				<TableActionHeader />
				<div className={styles.body}>
					<CustomTable
						dataSource={filteredData}
						isLoading={isLoading}
						search={search}
						performSearchHandler={performSearchHandler}
						openDrawerHandler={openDrawerHandler}
						setDrawerInfoHandler={setDrawerInfoHandler}
						setEditSelectedCategory={setEditSelectedCategory}
						isSearchCancel={isSearchCancel}
						setSearch={setSearch}
						setIsSearchCancel={setIsSearchCancel}
					/>
				</div>
			</div>
			{isSideDrawerOpen && (
				<SideDrawerWrapper
					isOpen={drawerAnimation}
					removeDrawerFromDom={removeDrawerFromDom}
					closeDrawerByAnimation={closeDrawerByAnimation}
					headerTitle={drawerTitle}
					position="right"
					width="half"
				>
					<EditMappingDrawerBody
						selectedCategory={editSelectedCategory}
						closeDrawerByAnimation={closeDrawerByAnimation}
						fetchTableData={fetchTableData}
					/>
				</SideDrawerWrapper>
			)}
		</>
	);
};

export default CategoryDoseFormMappingTable;
